import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { RefreshCw, Copy, CheckCircle2, ShieldCheck, SearchCheck, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatIDR = (v) => `Rp ${Number(v || 0).toLocaleString('id-ID')}`;

// QRISly returns expiry as "YYYY-MM-DD HH:MM:SS" in WIB (UTC+7).
// Convert to a proper Date in local time for countdown.
const parseWibExpiry = (s) => {
  if (!s) return null;
  try {
    // Treat input as WIB, then convert to UTC epoch.
    const iso = s.replace(' ', 'T');
    const asUtc = new Date(iso + '+07:00');
    return isNaN(asUtc.getTime()) ? null : asUtc;
  } catch {
    return null;
  }
};

const fmtCountdown = (ms) => {
  if (ms <= 0) return '00:00';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

export default function QrisCheckout() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const orderId = params.get('order');

  const [qris, setQris] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [paid, setPaid] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [checkingPaid, setCheckingPaid] = useState(false);
  // Tracks whether buyer has clicked "I already paid" — used to show a help panel
  // if QRISly still reports unpaid after a reasonable delay.
  const [manualCheckTried, setManualCheckTried] = useState(false);
  const pollRef = useRef(null);

  const loadQris = async (opts = {}) => {
    try {
      if (opts.regen) setRegenerating(true);
      else setLoading(true);
      setError('');
      const res = await axios.get(`${API}/orders/${orderId}/qris`);
      const data = res.data;
      if (data.payment_status === 'paid' || data.payment_status === 'completed') {
        setPaid(true);
        setQris(data);
        return;
      }
      if (data.payment_status === 'failed' || data.payment_status === 'expired' || data.payment_status === 'cancelled') {
        navigate(`/session-expired?reason=${data.payment_status}&order=${orderId}`, { replace: true });
        return;
      }
      setQris(data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate(`/session-expired?reason=auth&order=${orderId}`, { replace: true });
        return;
      }
      setError(err.response?.data?.detail || 'Unable to load QR code. Please try again.');
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  // Initial load + auth gate
  useEffect(() => {
    if (authLoading) return;
    if (!orderId) { navigate('/', { replace: true }); return; }
    if (!isAuthenticated) {
      navigate(`/session-expired?reason=auth&order=${orderId}`, { replace: true });
      return;
    }
    loadQris();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, orderId]);

  // 1s ticker for countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Poll payment status every 4s while showing QR
  useEffect(() => {
    if (paid || !qris?.qris_string) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/orders/check-status/${orderId}`);
        const s = res.data?.payment_status;
        if (s === 'paid' || s === 'completed') {
          setPaid(true);
          clearInterval(pollRef.current);
        } else if (s === 'failed' || s === 'expired' || s === 'cancelled') {
          clearInterval(pollRef.current);
          navigate(`/session-expired?reason=${s}&order=${orderId}`, { replace: true });
        }
      } catch {/* ignore polling errors */}
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [qris?.qris_string, paid, navigate, orderId]);

  const expiryDate = qris?.expiry_time ? parseWibExpiry(qris.expiry_time) : null;
  const msLeft = expiryDate ? expiryDate.getTime() - now : 0;
  const expired = expiryDate && msLeft <= 0;

  // NOTE: We deliberately do NOT auto-regenerate when the QR expires. If the buyer
  // already scanned the QR but hasn't tapped "Pay" yet, silently swapping the code
  // would mean their payment lands on an expired history_id that QRISly won't
  // track — creating stuck orders. Instead we show an explicit button.

  const handleCopy = async () => {
    if (!qris?.qris_string) return;
    try {
      await navigator.clipboard.writeText(qris.qris_string);
      setCopied(true);
      toast.success('QRIS code copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed. Please screenshot the QR instead.');
    }
  };

  // Buyer-initiated force-check: hits the backend which directly queries QRISly
  // for the latest status, independent of the 4s poll loop. Useful when the
  // webhook is delayed or the buyer isn't sure the poll caught their payment.
  const handleIAlreadyPaid = async () => {
    if (!orderId || checkingPaid) return;
    setCheckingPaid(true);
    try {
      const res = await axios.get(`${API}/orders/check-status/${orderId}`);
      const s = res.data?.payment_status;
      if (s === 'paid' || s === 'completed') {
        setPaid(true);
      } else if (s === 'failed' || s === 'expired' || s === 'cancelled') {
        navigate(`/session-expired?reason=${s}&order=${orderId}`, { replace: true });
      } else {
        setManualCheckTried(true);
        toast.info("We still don't see your payment. Give it another ~30 seconds and try again.");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate(`/session-expired?reason=auth&order=${orderId}`, { replace: true });
      } else {
        toast.error("Couldn't verify right now. Please try again in a moment.");
      }
    } finally {
      setCheckingPaid(false);
    }
  };

  if (paid) {
    return (
      <div className="min-h-screen bg-[#FCFAF8]"><Header />
        <div className="max-w-xl mx-auto px-4 py-12 text-center" data-testid="qris-paid-state">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="text-green-700" size={32} />
          </div>
          <h1 className="font-display text-3xl text-[#6B1F2B] mb-2">Payment received!</h1>
          <p className="text-[#78716C] mb-6">Your order has been confirmed. The seller is being notified to ship your item.</p>
          <Button
            onClick={() => navigate('/orders')}
            className="bg-[#6B1F2B] hover:bg-[#5A1824] text-white rounded-full px-8"
            data-testid="qris-view-order-btn"
          >View order</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFAF8]"><Header />
      <div className="max-w-xl mx-auto px-4 py-8" data-testid="qris-checkout-page">
        <button
          onClick={() => navigate('/orders')}
          className="text-sm text-[#78716C] hover:text-[#6B1F2B] mb-4"
          data-testid="qris-back-btn"
        >← Back to Orders</button>

        <div className="bg-white border border-cream-200 rounded-3xl p-6 shadow-sm">
          <div className="text-center mb-5">
            <p className="text-xs uppercase tracking-wider text-[#78716C]">Pay with QRIS</p>
            <h1 className="font-display text-2xl text-[#6B1F2B] mt-1">Scan to pay</h1>
            <p className="text-sm text-[#78716C] mt-1">Open any QRIS-enabled app — GoPay, OVO, DANA, ShopeePay, BCA, etc.</p>
          </div>

          {loading && (
            <div className="flex flex-col items-center py-10" data-testid="qris-loading">
              <div className="w-10 h-10 border-4 border-[#F4C2C2] border-t-[#6B1F2B] rounded-full animate-spin mb-3" />
              <p className="text-sm text-[#78716C]">Generating your QR code…</p>
            </div>
          )}

          {error && !loading && (
            <div className="py-6 text-center" data-testid="qris-error">
              <p className="text-sm text-red-600 mb-4">{error}</p>
              <Button
                onClick={() => loadQris({ regen: true })}
                variant="outline"
                className="rounded-full"
                data-testid="qris-retry-btn"
              >Try again</Button>
            </div>
          )}

          {!loading && !error && qris?.qris_string && (
            <>
              <div className="flex justify-center relative">
                <div className={`p-4 bg-white border-2 rounded-2xl ${regenerating ? 'opacity-40' : ''} ${expired ? 'border-red-300 opacity-40' : 'border-cream-200'}`}>
                  <QRCodeCanvas
                    value={qris.qris_string}
                    size={240}
                    level="M"
                    includeMargin={false}
                    data-testid="qris-code-canvas"
                  />
                </div>
                {expired && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="bg-red-600 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full shadow-md" data-testid="qris-expired-badge">
                      Expired
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-5 text-center">
                <p className="text-xs uppercase tracking-wider text-[#78716C]">Total to pay</p>
                <p className="font-display text-3xl text-[#6B1F2B] mt-1" data-testid="qris-amount">
                  {formatIDR(qris.final_amount)}
                </p>
                {qris.original_amount && qris.final_amount !== qris.original_amount && (
                  <p className="text-xs text-[#78716C] mt-1">
                    Order total {formatIDR(qris.original_amount)} + QRIS service fee {formatIDR(qris.final_amount - qris.original_amount)}
                  </p>
                )}
              </div>

              {expired ? (
                <div className="mt-5 rounded-2xl border-2 border-red-200 bg-red-50 p-4 text-center" data-testid="qris-expired-panel">
                  <p className="text-sm font-semibold text-red-700 mb-1">This QR expired</p>
                  <p className="text-xs text-red-600 mb-3">
                    Don't pay this QR anymore — tap Regenerate to get a fresh one with a new 15-minute window.
                  </p>
                  <Button
                    onClick={() => loadQris({ regen: true })}
                    disabled={regenerating}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6"
                    data-testid="qris-regenerate-expired-btn"
                  >
                    <RefreshCw size={14} className={`mr-1.5 ${regenerating ? 'animate-spin' : ''}`} />
                    {regenerating ? 'Generating…' : 'Regenerate QR'}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mt-5 flex items-center justify-center gap-3 text-sm">
                    <span className="text-[#78716C]" data-testid="qris-countdown">
                      Expires in <span className="font-mono text-[#6B1F2B] font-medium">{fmtCountdown(msLeft)}</span>
                    </span>
                    <button
                      onClick={() => loadQris({ regen: true })}
                      disabled={regenerating}
                      className="inline-flex items-center gap-1 text-[#6B1F2B] hover:underline disabled:opacity-50"
                      data-testid="qris-regenerate-btn"
                    >
                      <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
                      Regenerate
                    </button>
                  </div>

                  <div className="mt-5 flex items-center justify-center gap-2">
                    <button
                      onClick={handleCopy}
                      className="inline-flex items-center gap-1.5 text-xs text-[#78716C] hover:text-[#6B1F2B] border border-cream-200 rounded-full px-3 py-1.5"
                      data-testid="qris-copy-btn"
                    >
                      {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                      {copied ? 'Copied!' : 'Copy QR string'}
                    </button>
                  </div>
                </>
              )}

              <div className="mt-6 pt-5 border-t border-cream-200 space-y-4">
                <div className="text-xs text-[#78716C] flex items-start gap-2">
                  <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#6B1F2B]" />
                  <p>
                    <strong>Keep this page open until you see "Payment received".</strong> After you tap Pay in your wallet app, it usually takes 5-15 seconds. If it takes longer than 30 seconds, tap the button below.
                  </p>
                </div>

                <div className="flex flex-col items-center gap-2">
                  <Button
                    onClick={handleIAlreadyPaid}
                    disabled={checkingPaid}
                    variant="outline"
                    className="rounded-full border-[#6B1F2B] text-[#6B1F2B] hover:bg-[#6B1F2B] hover:text-white"
                    data-testid="qris-i-paid-btn"
                  >
                    <SearchCheck size={14} className={`mr-1.5 ${checkingPaid ? 'animate-pulse' : ''}`} />
                    {checkingPaid ? 'Checking…' : 'I already paid — check again'}
                  </Button>
                </div>

                {manualCheckTried && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="qris-stuck-help">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Payment not showing up yet?</p>
                      <p className="mb-1.5">
                        Most payments confirm within 30 seconds. If you've waited longer and see the money left your wallet but this page is still stuck, please contact our admin:
                      </p>
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/orders/${orderId}/escalate-payment`);
                            const cid = res.data?.conversation_id;
                            if (cid) {
                              navigate(`/messages/${cid}`);
                            } else {
                              navigate('/messages');
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Couldn't open the chat. Please try /messages directly.");
                            navigate('/messages');
                          }
                        }}
                        className="text-amber-900 underline font-semibold"
                        data-testid="qris-contact-support-btn"
                      >
                        Open chat with admin →
                      </button>
                      <p className="mt-1.5 text-xs opacity-80">A new chat will open with order context pre-filled. Share your payment screenshot/transaction reference there — we can reconcile it manually.</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
